import { useAtom } from "jotai";
import React, { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { LOCAL_STORAGE_KEYS } from "../../consts";
import { currentUserAtom } from "../../jotai/atoms";
import { useAuth } from "../../auth/useAuth";
import Loader from "../../components/Loader";
import { DialogTakeover } from "../../components/Dialog";
import { createUser } from "./user";
import { NameForm } from "./EditUser";

export default function Onboarding({ children }: React.PropsWithChildren<{}>) {
  const [currentUser, setCurrentUser] = useAtom(currentUserAtom);
  const { auth, hasFetched } = useAuth();

  // sets currentUser's id, which can be a did (decentralized ID from auth/magic link), or randomized UDID
  // currentUser should reactively change if auth comes in later (e.g. you log in on another tab, slow network)
  // but it prioritizes loading anonymous immediately so anonymous users dont have to wait for auth
  const setUsername = useCallback(
    (username: string) => {
      const newUser = createUser(username, auth?.issuer);
      window.localStorage.setItem(LOCAL_STORAGE_KEYS.USERNAME, username);

      setCurrentUser((current) => {
        Sentry.setUser({ username: newUser.id });
        if (current?.id === newUser.id) return current; // dont reset user if same ID
        if (current?.anonymous && newUser.anonymous) return current; // dont reset anonymous user if already anonymous
        // always reset these to off on page load
        newUser.audio = false;
        newUser.video = false;
        console.log("setting current user", newUser);
        return newUser;
      });
    },
    [setCurrentUser, auth]
  );

  // skip dialog if existing username
  const [existingUsername] = useState<string | null>(() =>
    window.localStorage.getItem(LOCAL_STORAGE_KEYS.USERNAME)
  );
  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    // param is a string!
    let name = urlParams.get("name");
    if (name) {
      setUsername(name);
    }
  }, [setUsername]);
  useEffect(() => {
    if (existingUsername && hasFetched) setUsername(existingUsername);
  }, [existingUsername, setUsername, hasFetched]);

  if (existingUsername && !currentUser) return <Loader />; // Prevent flashing dialog for initial render

  if (currentUser) return <>{children}</>;

  return (
    <DialogTakeover
      title="What's your name?"
      message="Enter a name or handle so people know how you’d like to be called. Your name will only be visible to others in spaces you joined."
    >
      <NameForm callback={setUsername} label="Enter" />
    </DialogTakeover>
  );
}
